/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useEffect, useMemo, ReactNode, useState, useContext } from "react";
import { useAppSelector } from "../redux/hooks";
import { buildHrmsPermissionTree } from "../utils/format-permissions";

// Define the types for permissions
type PermissionActions = "canEdit" | "canView" | "canAdd" | "canDelete" | "canAddForOthers" | "canViewById";
type SubModulePermissions = Partial<Record<PermissionActions, boolean>>;
type ModulePermissions = SubModulePermissions & Record<string, SubModulePermissions>;

export interface Permission {
  [key: string]: any;
}

export interface PermissionContextType {
  [module: string]: ModulePermissions;
}

interface PermissionProviderProps {
  children: ReactNode;
}

// Create a context with a default value
export const PermissionContext = createContext<PermissionContextType | null>(null);

const ROLE_PERMISSION_STORAGE_KEY = "role-permission";
const parseStoredPermissions = (rawValue: string | null): Record<string, any> => {
  if (!rawValue) {
    return {};
  }

  try {
    const parsedPermissions = JSON.parse(rawValue);
    return parsedPermissions;
  } catch (error) {
    localStorage.removeItem(ROLE_PERMISSION_STORAGE_KEY);
    return {};
  }
};

export const PermissionsProvider: React.FC<PermissionsProviderProps> = ({ children }) => {
  const { setIsPermissionLoaded = false } = useAppSelector(
    (store) => store.roles ?? {}
  );

  const [storagePermission, setStoragePermission] = useState<string | null>(() => {
    if (typeof window === "undefined") {
      return null;
    }
    return window.localStorage.getItem(ROLE_PERMISSION_STORAGE_KEY);
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const syncPermissions = () => {
      const latestPermission = window.localStorage.getItem(ROLE_PERMISSION_STORAGE_KEY);
      setStoragePermission(latestPermission);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (!event.key || event.key === ROLE_PERMISSION_STORAGE_KEY) {
        syncPermissions();
      }
    };

    syncPermissions();
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("role-permission-changed", syncPermissions);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("role-permission-changed", syncPermissions);
    };
  }, []);

  const permissions: Record<string, any> = useMemo(() => {
    return parseStoredPermissions(storagePermission);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsPermissionLoaded, storagePermission]);
  
  const computedPermissions = useMemo(() => {
    return buildHrmsPermissionTree(permissions);
  }, [permissions]);

  return (
    <PermissionContext.Provider value={computedPermissions}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook to use the PermissionContext
export const usePermissionContext = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

// Keep legacy exports for backward compatibility
export const PermissionsContext = PermissionContext;
export const PermissionProvider = PermissionsProvider;
export type PermissionsContextType = PermissionContextType;
